<template>
  <div data-disable-subpage-swipe="true" class="rounded-2xl" :class="wrapperClasses">
    <BaseSwiper
      ref="carouselSwiper"
      :id="swiperId"
      v-slot="slotProps"
      :items="items"
      :options="swiperOptions"
      :gap="0"
      :space-between="carouselTransitionGap"
      class="image-carousel-swiper"
      @active-index-change="handleSlideChange"
    >
      <div class="w-full box-border">
        <button
          type="button"
          class="image-carousel-card relative block shrink-0 rounded-xl overflow-hidden transition-opacity"
          :class="[cardClasses, canOpenLightbox ? 'active:opacity-70' : 'cursor-default']"
          @click="openLightbox(slotProps.index)"
        >
          <img
            v-if="!isVideoItem(slotProps.item)"
            :src="slotProps.item.image?.url"
            :alt="slotProps.item.image?.alt || slotProps.item.label || ''"
            class="block h-auto w-full max-w-full object-contain"
            @load="handleMediaLoaded"
          />
          <video
            v-else
            :src="slotProps.item.image?.url"
            autoplay
            loop
            muted
            playsinline
            class="block h-auto w-full max-w-full object-contain"
            @loadedmetadata="handleMediaLoaded"
          />
        </button>
      </div>
    </BaseSwiper>

    <div v-if="items.length > 1" class="pb-4 pt-2">
      <Pagination
        :total-pages="items.length"
        :current-page="currentIndex + 1"
        passive-color="#D3D8DE"
        active-color="#6B5744"
        :active-width="16"
      />
    </div>

    <div v-if="showDownloadAllButton" class="pb-4" :class="{ 'pt-4': isSingleItem }">
      <button
        type="button"
        class="rounded-full inline-flex w-full border border-solid bg-transparent appearance-none py-2 px-4 items-center justify-center gap-x-2 cursor-pointer text-center font-bold text-[1rem] leading-[1.5rem] transition-opacity duration-200 active:scale-[0.98] active:opacity-80"
        :class="isLightMode ? 'border-[#21201E]' : 'border-[#ECEBEB]'"
        :disabled="isDownloading || isDownloadingAll"
        @click.stop="downloadAll"
      >
        <div
          v-if="isDownloadingAll"
          class="size-5 border-2 border-current border-t-transparent rounded-full animate-spin"
        />
        <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            :d="DOWNLOAD_ICON_PATH"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>{{
          isDownloadingAll ? 'Saving...' : isSingleItem ? 'Download' : 'Download all'
        }}</span>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="fixed inset-0 bg-black/80 z-[9999] flex flex-col"
        @click="closeLightbox"
      >
        <div class="px-4" :style="lightboxHeaderStyle" @click.stop>
          <div class="flex items-stretch justify-between gap-3">
            <button
              type="button"
              class="size-10 flex items-center justify-center rounded-full bg-white/20 active:opacity-70"
              @click="closeLightbox"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <button
              v-if="hasDownloadableItems && !isSingleItem"
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-full border border-[#ECEBEB] bg-white/15 px-3 py-2 text-white active:opacity-70"
              :disabled="isDownloading || isDownloadingAll"
              @click="downloadAll"
            >
              <div
                v-if="isDownloadingAll"
                class="size-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              />
              <svg v-else width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path
                  :d="DOWNLOAD_ICON_PATH"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="text-[0.75rem] font-semibold">
                {{ isDownloadingAll ? 'Downloading...' : 'Download all' }}
              </span>
            </button>
            <div v-else class="size-10" />
          </div>
        </div>

        <div
          ref="lightboxViewport"
          class="flex-1 flex items-center justify-center px-4 overflow-hidden"
          @click.stop
          @wheel.prevent="handleLightboxWheel"
          @touchstart="handleLightboxTouchStart"
          @touchmove.prevent="handleLightboxTouchMove"
          @touchend="handleLightboxTouchEnd"
          @touchcancel="handleLightboxTouchEnd"
        >
          <BaseSwiper
            ref="lightboxSwiper"
            :id="lightboxSwiperId"
            :items="items"
            :options="lightboxOptions"
            :gap="0"
            class="h-full w-full"
            @active-index-change="handleLightboxSlideChange"
          >
            <template #default="slotProps">
              <div class="flex h-full w-full items-center justify-center overflow-hidden">
                <div
                  class="flex items-center justify-center overflow-hidden rounded-2xl"
                  :class="lightboxContainerClasses"
                  :style="lightboxZoomStageStyle"
                >
                  <img
                    v-if="!isVideoItem(slotProps.item)"
                    :src="slotProps.item.image?.url"
                    :alt="slotProps.item.image?.alt || slotProps.item.label || ''"
                    class="h-full w-full object-contain"
                    @load="handleMediaLoaded"
                  />
                  <video
                    v-else
                    :src="slotProps.item.image?.url"
                    autoplay
                    loop
                    muted
                    playsinline
                    class="h-full w-full object-contain"
                    @loadedmetadata="handleMediaLoaded"
                  />
                </div>
              </div>
            </template>
          </BaseSwiper>
        </div>

        <div class="pb-8 pt-4" @click.stop>
          <div class="flex justify-center gap-2">
            <button
              v-if="hasDownloadableItems"
              type="button"
              class="flex items-center gap-2 rounded-full border border-[#ECEBEB] bg-white/20 px-4 py-2 active:opacity-70"
              :disabled="isDownloading || isDownloadingAll"
              @click="downloadCurrent"
            >
              <div
                v-if="isDownloading"
                class="size-5 border-2 border-white border-t-transparent rounded-full animate-spin"
              />
              <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  :d="DOWNLOAD_ICON_PATH"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="text-white text-[0.875rem]">Download</span>
            </button>
          </div>
          <div v-if="items.length > 1" class="flex justify-center pt-3">
            <div class="rounded-full bg-white/15 px-3 py-2 text-[0.75rem] font-semibold text-white">
              {{ lightboxDisplayIndex }} / {{ items.length }}
            </div>
          </div>
        </div>
      </div>
      <Toast safe-area :z-index="10000" />
    </Teleport>
  </div>
</template>

<script>
import { triggerNotification } from '@/utils/haptics';
import { canSaveToPhotos, saveToPhotos } from '@/utils/save-to-photos';
import { mapState } from 'vuex';
import Toast from '@/components/common/Toast.vue';
import BaseSwiper from '@/components/design/BaseSwiper.vue';
import Pagination from '@/components/design/Pagination.vue';
import { SINGLE_ITEM } from '@/utils/swiper-presets';
import { isIosNativeOnly, isAndroidNativeOnly, isTablet } from '@/utils/platform-check';

export default {
  name: 'ImageCarousel',
  components: { Toast, BaseSwiper, Pagination },
  props: {
    title: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: String,
      default: 'wide',
    },
    items: {
      type: Array,
      default: () => [],
    },
    enableZoom: {
      type: Boolean,
      default: true,
    },
    enableDownload: {
      type: Boolean,
      default: true,
    },
    interactionMode: {
      type: String,
      default: '',
      validator: value =>
        ['', 'view-only', 'view-and-zoom', 'view-zoom-and-download'].includes(value),
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      DOWNLOAD_ICON_PATH: 'M10 3v10M6 9l4 4 4-4M3 15v1a1 1 0 001 1h12a1 1 0 001-1v-1',
      lightboxOpen: false,
      lightboxIndex: 0,
      isDownloading: false,
      isDownloadingAll: false,
      currentIndex: 0,
      zoomScale: 1,
      minZoomScale: 1,
      maxZoomScale: 3,
      translateX: 0,
      translateY: 0,
      pinchStartDistance: null,
      pinchStartScale: 1,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      isPanning: false,
      swiperId: `image-carousel-${Math.random().toString(36).substring(2, 11)}`,
      lightboxSwiperId: `lightbox-${Math.random().toString(36).substring(2, 11)}`,
    };
  },
  computed: {
    ...mapState('eventPage', ['isLightMode']),
    cardClasses() {
      return 'mx-auto w-full max-w-full';
    },
    downloadableItems() {
      return this.items.filter(item => item?.image?.url);
    },
    normalizedInteractionMode() {
      if (this.interactionMode) return this.interactionMode;
      if (this.enableDownload) return 'view-zoom-and-download';
      if (this.enableZoom) return 'view-and-zoom';
      return 'view-only';
    },
    hasDownloadableItems() {
      return (
        this.normalizedInteractionMode === 'view-zoom-and-download' &&
        this.downloadableItems.length > 0
      );
    },
    wrapperClasses() {
      return this.embedded
        ? ''
        : ['shadow-elevation-1 mx-5 mt-4', this.isLightMode ? 'bg-white' : 'bg-[#2B2926]'];
    },
    canOpenLightbox() {
      return this.normalizedInteractionMode !== 'view-only';
    },
    showDownloadAllButton() {
      return this.hasDownloadableItems;
    },
    isSingleItem() {
      return this.items.length === 1;
    },
    carouselTransitionGap() {
      return this.isSingleItem ? 0 : 12;
    },
    lightboxDisplayIndex() {
      return this.lightboxIndex + 1;
    },
    lightboxContainerClasses() {
      return 'h-[calc(100vh-176px)] max-h-full w-full';
    },
    lightboxZoomStageStyle() {
      return {
        transform: `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.zoomScale})`,
        transformOrigin: 'center center',
        transition: this.isPanning || this.pinchStartDistance ? 'none' : 'transform 0.18s ease',
        touchAction: this.zoomScale > 1 ? 'none' : 'pan-y pinch-zoom',
      };
    },
    swiperOptions() {
      return {
        ...SINGLE_ITEM,
        // Each photo keeps its own height; mixed 16:9 / 9:16 galleries resize per slide.
        autoHeight: true,
        grabCursor: !this.isSingleItem,
        allowTouchMove: !this.isSingleItem,
        hasNavigation: false,
      };
    },
    lightboxOptions() {
      return {
        ...SINGLE_ITEM,
        initialSlide: this.lightboxIndex,
        allowTouchMove:
          !this.isSingleItem &&
          (this.normalizedInteractionMode === 'view-only' || this.zoomScale === 1),
        hasNavigation: false,
      };
    },
    lightboxHeaderStyle() {
      let paddingTop = '32px';
      if (isIosNativeOnly() || isTablet()) {
        paddingTop = 'calc(32px + env(safe-area-inset-top, 0px))';
      } else if (isAndroidNativeOnly()) {
        paddingTop = 'max(32px, env(safe-area-inset-top, 32px))';
      }
      return { paddingTop };
    },
  },
  watch: {
    zoomScale() {
      this.$nextTick(() => {
        this.updateLightboxSwiperTouchState();
      });
    },
  },
  methods: {
    isVideoItem(item) {
      // Detect any supported video extension (incl. external/S3 URLs) so a
      // gallery item pointing at an .mp4/.mov/etc. renders as <video>, not <img>.
      return item?.image?.url?.match(/\.(mp4|mov|webm|ogg|avi|m4v)([?#].*)?$/i) ?? false;
    },
    handleMediaLoaded() {
      this.$nextTick(() => {
        this.$refs.carouselSwiper?.update?.();
        this.$refs.lightboxSwiper?.update?.();
      });
    },
    handleSlideChange(_isEnd, index) {
      this.currentIndex = index - 1;
    },
    handleLightboxSlideChange(_isEnd, index) {
      this.lightboxIndex = index - 1;
      this.resetZoom();
    },
    openLightbox(index) {
      if (!this.canOpenLightbox) return;
      this.lightboxIndex = index;
      this.resetZoom();
      this.lightboxOpen = true;
      this.$nextTick(() => {
        this.updateLightboxSwiperTouchState();
      });
    },
    closeLightbox() {
      this.resetZoom();
      this.lightboxOpen = false;
    },
    resetZoom() {
      this.zoomScale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.pinchStartDistance = null;
      this.pinchStartScale = 1;
      this.isPanning = false;
    },
    getTouchDistance(touchA, touchB) {
      return Math.hypot(touchB.clientX - touchA.clientX, touchB.clientY - touchA.clientY);
    },
    clampScale(scale) {
      return Math.min(Math.max(scale, this.minZoomScale), this.maxZoomScale);
    },
    getPanLimits() {
      const viewport = this.$refs.lightboxViewport;
      if (!viewport) return { x: 0, y: 0 };

      const { clientWidth, clientHeight } = viewport;
      const scaledWidth = clientWidth * this.zoomScale - clientWidth;
      const scaledHeight = clientHeight * this.zoomScale - clientHeight;
      return {
        x: Math.max(0, scaledWidth / 2),
        y: Math.max(0, scaledHeight / 2),
      };
    },
    clampTranslation(x, y) {
      const limits = this.getPanLimits();
      return {
        x: Math.min(Math.max(x, -limits.x), limits.x),
        y: Math.min(Math.max(y, -limits.y), limits.y),
      };
    },
    applyScale(nextScale) {
      this.zoomScale = this.clampScale(nextScale);

      if (this.zoomScale === 1) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }

      const clamped = this.clampTranslation(this.translateX, this.translateY);
      this.translateX = clamped.x;
      this.translateY = clamped.y;
    },
    updateLightboxSwiperTouchState() {
      const swiperInstance = this.$refs.lightboxSwiper?.swiper;
      if (!swiperInstance) return;

      swiperInstance.allowTouchMove =
        !this.isSingleItem &&
        (this.normalizedInteractionMode === 'view-only' || this.zoomScale === 1);
    },
    handleLightboxWheel(event) {
      if (this.normalizedInteractionMode === 'view-only') return;
      const delta = event.deltaY < 0 ? 0.2 : -0.2;
      this.applyScale(this.zoomScale + delta);
    },
    handleLightboxTouchStart(event) {
      if (this.normalizedInteractionMode === 'view-only') return;

      if (event.touches.length === 2) {
        event.stopPropagation();
        this.pinchStartDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
        this.pinchStartScale = this.zoomScale;
        this.isPanning = false;
        return;
      }

      if (event.touches.length === 1 && this.zoomScale > 1) {
        event.stopPropagation();
        this.isPanning = true;
        this.panStartX = event.touches[0].clientX;
        this.panStartY = event.touches[0].clientY;
        this.panOriginX = this.translateX;
        this.panOriginY = this.translateY;
      }
    },
    handleLightboxTouchMove(event) {
      if (this.normalizedInteractionMode === 'view-only') return;

      if (event.touches.length === 2 && this.pinchStartDistance) {
        event.stopPropagation();
        const distance = this.getTouchDistance(event.touches[0], event.touches[1]);
        const scale = this.pinchStartScale * (distance / this.pinchStartDistance);
        this.applyScale(scale);
        return;
      }

      if (event.touches.length === 1 && this.isPanning && this.zoomScale > 1) {
        event.stopPropagation();
        const deltaX = event.touches[0].clientX - this.panStartX;
        const deltaY = event.touches[0].clientY - this.panStartY;
        const clamped = this.clampTranslation(this.panOriginX + deltaX, this.panOriginY + deltaY);
        this.translateX = clamped.x;
        this.translateY = clamped.y;
      }
    },
    handleLightboxTouchEnd(event) {
      if (this.normalizedInteractionMode === 'view-only') return;

      if (event.touches.length < 2) {
        this.pinchStartDistance = null;
      }

      if (event.touches.length === 0) {
        this.isPanning = false;
      }
    },
    buildDownloadMessage(count, isSuccess) {
      const isNative = canSaveToPhotos();
      const verb = isNative ? 'Saved' : 'Downloaded';
      const noun = count === 1 ? '1 file' : `${count} files`;
      const suffix = isNative ? 'to Photos' : '';

      if (isSuccess) {
        return `${verb} ${noun}${suffix ? ' ' + suffix : ''}`;
      }
      if (count > 0) {
        return `${verb} ${noun} before download failed`;
      }
      return isNative ? 'Failed to save files' : 'Failed to download files';
    },
    async downloadBrowserFile(url, index) {
      const link = document.createElement('a');
      const proxyUrl = `${
        process.env.VUE_APP_API_URL
      }/storyblok/download-asset?url=${encodeURIComponent(url)}&index=${index + 1}`;

      link.href = proxyUrl;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async downloadCurrent() {
      if (this.normalizedInteractionMode !== 'view-zoom-and-download') return;
      const item = this.items[this.lightboxIndex];
      const url = item?.image?.url;
      if (!url || this.isDownloading || this.isDownloadingAll) return;

      if (!canSaveToPhotos()) {
        await this.downloadBrowserFile(url, this.lightboxIndex);
        return;
      }

      this.isDownloading = true;
      try {
        await saveToPhotos(url, { isVideo: this.isVideoItem(item) });
        await triggerNotification('success');
        this.$store.dispatch('toast/success', 'Saved to Photos');
      } catch (error) {
        const errorMsg = error?.message || JSON.stringify(error) || 'Unknown error';
        console.error('[ImageCarousel] Failed to save file:', errorMsg, error);
        this.$store.dispatch('toast/error', `Error: ${errorMsg}`);
      } finally {
        this.isDownloading = false;
      }
    },
    async downloadAll() {
      if (this.normalizedInteractionMode !== 'view-zoom-and-download') return;
      if (!this.hasDownloadableItems || this.isDownloading || this.isDownloadingAll) return;

      this.isDownloadingAll = true;
      let savedCount = 0;
      const canSaveNatively = canSaveToPhotos();

      try {
        for (const [index, item] of this.downloadableItems.entries()) {
          if (!canSaveNatively) {
            await this.downloadBrowserFile(item.image.url, index);
          } else {
            await saveToPhotos(item.image.url, { isVideo: this.isVideoItem(item) });
          }
          savedCount += 1;
        }

        await triggerNotification('success');
        this.$store.dispatch('toast/success', this.buildDownloadMessage(savedCount, true));
      } catch (error) {
        const errorMsg = error?.message || JSON.stringify(error) || 'Unknown error';
        console.error('[ImageCarousel] Download all failed:', errorMsg, error);
        this.$store.dispatch('toast/error', `Error (${savedCount} saved): ${errorMsg}`);
      } finally {
        this.isDownloadingAll = false;
      }
    },
  },
};
</script>
